import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AggregatorOption, BankRow, BankFormData, getGatewayParam } from '../types/bankdetails';

interface BankFormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onSubmit: (formData: BankFormData) => void;
  aggregator: AggregatorOption;
  mode: 'add' | 'edit';
  editingIndex?: number | null;
  rows?: BankRow[];
  onOpenHistory?: (bank: BankRow) => void;
}

export function BankFormDialog({
  open,
  onClose,
  title,
  onSubmit,
  aggregator,
  mode,
  editingIndex,
  rows,
  onOpenHistory,
}: BankFormDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState<BankFormData>({
    gateway: aggregator,
    name: '',
    ifscCode: '',
    nbBankId: '',
    nbHybridBankId: '',
    nbDebitCardBankId: '',
    nbLumpsum: true,
    nbHybrid: false,
    nbDebitCard: false,
    emdBankId: '',
    emdNetBanking: true,
    emdDebitCard: true,
    emdAadhaar: true,
    emdPan: true,
    remarks: '',
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    const hasValidIndex = typeof editingIndex === 'number' && editingIndex >= 0;
    if (open && mode === 'edit' && hasValidIndex && rows && rows[editingIndex]) {
      const row = rows[editingIndex]!;
      setFormData({
        gateway: getGatewayParam(aggregator),
        name: row.name,
        ifscCode: row.ifscCode,
        nbBankId: row.netbanking?.bankId || '-',
        nbHybridBankId: row.netbanking?.hybridBankId || '-',
        nbDebitCardBankId: row.netbanking?.lumpsumDebitCardBankId || '-',
        nbLumpsum: !!row.netbanking?.lumpsum,
        nbHybrid: !!row.netbanking?.hybrid,
        nbDebitCard: !!row.netbanking?.lumpsumDebitCard,
        emdBankId: row.emandate?.bankId || '-',
        emdNetBanking: !!row.emandate?.netBanking,
        emdDebitCard: !!row.emandate?.debitCard,
        emdAadhaar: !!row.emandate?.aadhaar,
        emdPan: !!row.emandate?.pan,
        remarks: '',
      });
    } else if (open && mode === 'add') {
      setFormData({
        gateway: aggregator,
        name: '',
        ifscCode: '',
        nbBankId: '',
        nbHybridBankId: '',
        nbDebitCardBankId: '',
        nbLumpsum: true,
        nbHybrid: false,
        nbDebitCard: false,
        emdBankId: '',
        emdNetBanking: true,
        emdDebitCard: true,
        emdAadhaar: true,
        emdPan: true,
        remarks: '',
      });
    }
  }, [open, mode, editingIndex, rows, aggregator]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.ifscCode) return;
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
            <TextField label="Payment Gateway" value={aggregator} disabled fullWidth />
            <TextField
              label="Bank Name"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Bank IFSC"
              value={formData.ifscCode}
              onChange={(e) => setFormData((p) => ({ ...p, ifscCode: e.target.value }))}
              fullWidth
            />
          </Box>

          <TableContainer
            component={Paper}
            sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}` }}
          >
            <Table
              size="small"
              sx={{
                '& th, & td': { border: `1px solid ${theme.palette.divider}` },
                '& thead th': {
                  backgroundColor: theme.palette.action.hover,
                  fontWeight: 600,
                  textAlign: 'center',
                },
                '& tbody td': { verticalAlign: 'middle' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ fontWeight: 600 }}>
                    Transactions
                  </TableCell>
                  <TableCell align="center" colSpan={5} sx={{ fontWeight: 600 }}>
                    Emandate / OTM
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Bank ID</TableCell>
                  <TableCell align="center">Lumpsum</TableCell>
                  <TableCell>Bank ID</TableCell>
                  <TableCell align="center">Hybrid / iSIP</TableCell>
                  <TableCell>Bank ID</TableCell>
                  <TableCell align="center">Debit Card</TableCell>
                  <TableCell>Bank ID</TableCell>
                  <TableCell align="center">Net Banking</TableCell>
                  <TableCell align="center">Debit Card</TableCell>
                  <TableCell align="center">Aadhaar</TableCell>
                  <TableCell align="center">PAN</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={formData.nbBankId}
                      placeholder="-"
                      inputProps={{ style: { textAlign: 'center' } }}
                      onChange={(e) => setFormData((p) => ({ ...p, nbBankId: e.target.value }))}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={formData.nbLumpsum}
                      onChange={(e) => setFormData((p) => ({ ...p, nbLumpsum: e.target.checked }))}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={formData.nbHybridBankId}
                      placeholder="-"
                      inputProps={{ style: { textAlign: 'center' } }}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, nbHybridBankId: e.target.value }))
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={formData.nbHybrid}
                      onChange={(e) => setFormData((p) => ({ ...p, nbHybrid: e.target.checked }))}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={formData.nbDebitCardBankId}
                      placeholder="-"
                      inputProps={{ style: { textAlign: 'center' } }}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, nbDebitCardBankId: e.target.value }))
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={formData.nbDebitCard}
                      onChange={(e) => setFormData((p) => ({ ...p, nbDebitCard: e.target.checked }))}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={formData.emdBankId}
                      placeholder="-"
                      inputProps={{ style: { textAlign: 'center' } }}
                      onChange={(e) => setFormData((p) => ({ ...p, emdBankId: e.target.value }))}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={formData.emdNetBanking}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, emdNetBanking: e.target.checked }))
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={formData.emdDebitCard}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, emdDebitCard: e.target.checked }))
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={formData.emdAadhaar}
                      onChange={(e) => setFormData((p) => ({ ...p, emdAadhaar: e.target.checked }))}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={formData.emdPan}
                      onChange={(e) => setFormData((p) => ({ ...p, emdPan: e.target.checked }))}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TextField
            label="Remarks"
            fullWidth
            multiline
            minRows={3}
            value={formData.remarks}
            onChange={(e) => setFormData((p) => ({ ...p, remarks: e.target.value }))}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {mode === 'edit' &&
          onOpenHistory &&
          typeof editingIndex === 'number' &&
          rows &&
          rows[editingIndex] && (
            <Button
              onClick={() => onOpenHistory(rows[editingIndex]!)}
              variant="outlined"
              color="info"
            >
              View History
            </Button>
          )}
        <Button onClick={handleSubmit} variant="contained">
          {mode === 'add' ? 'Add Bank' : 'Update Bank'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
